import { useCallback } from 'react';
import { useNavigate, useMatches } from '@tanstack/react-router';
import { getFeature } from '@/lib/guideRegistry';
import { useGuidesStore } from '@/stores/useGuidesStore';

interface Props {
  id: string;
}

export function FeatureRef({ id }: Props) {
  const feature = getFeature(id);
  const navigate = useNavigate();
  const matches = useMatches();
  const closeWalkthrough = useGuidesStore((s) => s.closeWalkthrough);

  const handleClick = useCallback(() => {
    if (!feature) return;
    closeWalkthrough();

    // In graduated phase, open the Guides Drawer at this feature instead of
    // navigating to Expand or dispatching the JourneyBar event.
    const phase = document.body.getAttribute('data-guide-phase');
    if (phase === 'graduated') {
      useGuidesStore.getState().openCatalogAtFeature(feature.id);
      return;
    }

    // True only if a concept-scoped Expand route is in the current match chain
    const onExpand = matches.some((m) => m.routeId === '/concepts/$conceptId/curate');

    if (onExpand) {
      // Already on Expand — dispatch directly; no sessionStorage needed
      window.dispatchEvent(
        new CustomEvent('guide:open-feature-panel', { detail: { id: feature.id } }),
      );
      return;
    }

    // Not on Expand — look for a concept-scoped match to extract conceptId from
    const conceptMatch = matches.find(
      (m) => (m.params as Record<string, unknown>).conceptId != null,
    );
    const conceptId = conceptMatch
      ? (conceptMatch.params as { conceptId: string }).conceptId
      : undefined;

    if (conceptId) {
      // Stash pending feature so Expand picks it up after navigation
      sessionStorage.setItem('paperchase-guide-pending-feature', feature.id);
      navigate({ to: '/concepts/$conceptId/curate', params: { conceptId } });
    } else {
      // No concept in the route chain — dashboard fallback, no stash
      navigate({ to: '/' });
    }
  }, [feature, matches, navigate, closeWalkthrough]);

  if (!feature) {
    if (import.meta.env.DEV) {
      console.warn(`<FeatureRef id="${id}"> — feature not found in registry`);
    }
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-200"
    >
      <feature.icon className="size-3" />
      {feature.content.label}
    </button>
  );
}
